import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { AsOfStrip } from '@/components/common/AsOfStrip';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { ContestedItemsTable } from '@/pages/anatel/conselho/ContestedItemsTable';
import { fetchCadeConselhoItems } from '@/services/cade';

/** Judged contested items the position estimates rest on. Reached from the bench, not the menu. */
export function CadeConselhoItemsPage() {
  const { t, i18n } = useTranslation(['cade', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['cade', 'conselho', 'items'],
    queryFn: fetchCadeConselhoItems,
  });
  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={8} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }
  const number = new Intl.NumberFormat(i18n.language);
  return (
    <div className="space-y-4 p-6">
      <Link to="/cade/conselho" className="inline-flex items-center gap-1 text-xs text-primary underline-offset-4 hover:underline">
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
        {t('cade:conselho.title')}
      </Link>
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cade:conselho.items.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cade:conselho.items.help')}</p>
      </header>
      <FieldGuide i18nKey="cade:conselho.itemsGuide" level={2} />
      <AsOfStrip asOf={data.as_of} />
      <p className="text-xs text-muted-foreground" data-total={data.total}>
        {number.format(data.total)}
      </p>
      <ContestedItemsTable items={data.items} ns="cade" processBase="/cade/c" relatorBase="/cade/r" />
    </div>
  );
}
